import React from 'react';
import { Box, Stack } from '@mui/material';
import { StatColumn } from './StatColumn';
import { StargraveStatLine } from '../../types';
import { StatModifier } from '../../utils/splitStats';
import { StatBreakdownPopover } from './StatBreakdownPopover';

export interface SharedStatValue {
  base: number;
  adjusted?: number;
  signed?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  details?: {
    label: string;
    modifiers: StatModifier[];
  };
}

export interface SharedMemberStatsAndHealthProps {
  stats: Record<keyof StargraveStatLine, SharedStatValue>;
  healthContent?: React.ReactNode;
  injuriesContent?: React.ReactNode;
  actionContent?: React.ReactNode;
  statusContent?: React.ReactNode;
}

const statKeys: Array<keyof StargraveStatLine> = ['move', 'fight', 'shoot', 'armour', 'will', 'health'];

export function buildStargraveSharedStats(
  baseStats: StargraveStatLine,
  adjustedStats: StargraveStatLine,
  getModifiers?: (stat: keyof StargraveStatLine) => StatModifier[]
): Record<keyof StargraveStatLine, SharedStatValue> {
  return statKeys.reduce((result, stat) => ({
    ...result,
    [stat]: {
      base: baseStats[stat],
      adjusted: adjustedStats[stat],
      signed: stat === 'fight' || stat === 'shoot' || stat === 'will',
      details: {
        label: stat[0].toUpperCase() + stat.slice(1),
        modifiers: getModifiers?.(stat) ?? [],
      },
    },
  }), {} as Record<keyof StargraveStatLine, SharedStatValue>);
}

/** One-line stat readout for mini cards (health is shown by the health bar). */
export function buildStargraveMiniStats(stats: StargraveStatLine): Array<{ label: string; value: string }> {
  const signed = (value: number) => `${value >= 0 ? '+' : ''}${value}`;
  return [
    { label: 'M', value: String(stats.move) },
    { label: 'F', value: signed(stats.fight) },
    { label: 'S', value: signed(stats.shoot) },
    { label: 'A', value: String(stats.armour) },
    { label: 'W', value: signed(stats.will) },
  ];
}

export const SharedMemberStatsAndHealth: React.FC<SharedMemberStatsAndHealthProps> = ({
  stats,
  healthContent,
  injuriesContent,
  actionContent,
  statusContent,
}) => {
  const [details, setDetails] = React.useState<{
    anchor: HTMLElement;
    label: string;
    base: number;
    adjusted: number;
    modifiers: StatModifier[];
  } | null>(null);

  return <>
    {statusContent}
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 0.5, flex: 1 }}>
        {statKeys.map((stat) => {
          const value = stats[stat];
          return <StatColumn
            key={stat}
            label={stat.slice(0, 1).toUpperCase()}
            value={value.base}
            adjustedValue={value.adjusted ?? value.base}
            signed={value.signed}
            onClick={value.onClick ?? (value.details ? (event) => setDetails({
              anchor: event.currentTarget,
              label: value.details!.label,
              base: value.base,
              adjusted: value.adjusted ?? value.base,
              modifiers: value.details!.modifiers,
            }) : undefined)}
          />;
        })}
      </Box>
      {actionContent}
    </Box>
    {healthContent && <Stack sx={{ mt: 0, pt: 0 }}>{healthContent}</Stack>}
    {injuriesContent}
    <StatBreakdownPopover popover={details} onClose={() => setDetails(null)} />
  </>
};
